// TicketPartsPanel — main Parts tab content.
// Composes rollup + per-job sections. Mutations gated by RequireFeature.
// Phase 5: also hosts the Parts Assist postMessage bridge + intake modal.

import { useEffect, useMemo, useState } from 'react';
// DTC-K: route the parts-capture `captured_at` write through the
// canonical clock so the parts-flow audit timestamp is deterministic
// under `clock.setFixed(...)`.
import { clock } from '@/utils/clock';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useFeatureFlag, useExposureFlag } from '@/hooks/useFeatureFlags';
import { RequireFeature } from '@/components/access/RequireFeature';
import { toast } from '@/hooks/use-toast';
import {
  useTicketPartsLines,
  useTicketPartsRollup,
  useCreatePartsLine,
  useUpdatePartsLine,
  useDeletePartsLine,
  useUpdatePartsLineStatus,
  type PartsLine,
  type PartsLineStatus,
  type PartsLineInsert,
} from '@/hooks/parts/usePartsLines';
import { PartsLineList } from './PartsLineList';
import { PartsRollupSummary } from './PartsRollupSummary';
import { PartsLineFormDialog, type JobOption } from './PartsLineFormDialog';
import { TicketProcurementPanel } from './TicketProcurementPanel';
import { JobsSection, type JobsSectionJob } from '@/components/tickets/parts-labor/JobsSection';
import { resolveAddJobSurface } from '@/lib/tickets/addJobSurface';
import { PartsLaborRollup } from '@/components/tickets/parts-labor/PartsLaborRollup';
import { TicketFinancialSummaryCard } from '@/components/tickets/parts-labor/TicketFinancialSummaryCard';
import { useTicketLaborLines } from '@/hooks/labor/useTicketLaborLines';
import { useTicketTechPayLines } from '@/hooks/labor/useTicketTechPayLines';
import { PartsAssistIntakeModal } from '@/components/tickets/parts-assist/PartsAssistIntakeModal';
import { CannedJobSelectorDialog } from '@/components/cannedJobs/CannedJobSelectorDialog';
import { useUserRole } from '@/hooks/useUserRole';
import { SuggestedPartsPanel } from './SuggestedPartsPanel';
import { useSuggestedParts } from '@/hooks/parts-suggestions/useSuggestedParts';
import type { PartSuggestion } from '@/lib/parts-suggestions/types';
import {
  isPartsAssistMessage,
  type PartsAssistCapturePayload,
} from '@/lib/parts-assist/messageContract';
import type { TaxabilityCategories, TaxSettingsLike, FeeRule } from '@/lib/parts-financial/calculator';

interface Props {
  ticketId: string;
}

// Default tax/fee context — neutral baseline when no Phase 1 settings exist.
// Real values come from the shop_tax_rules / shop_fee_rules tables when present.
const DEFAULT_TAX_RULES = {
  categories: {
    parts: true, labor: true, tires: true, shop_supplies: true,
    disposal: true, hazmat: true, core: false, sublet: false,
  } as TaxabilityCategories,
};
const DEFAULT_TAX_SETTINGS: TaxSettingsLike = { combined_rate: 0 };

export function TicketPartsPanel({ ticketId }: Props) {
  const { activeShop } = useAuth();
  const shopId = activeShop?.id;
  const partsLineItems = useFeatureFlag('partsLineItems');
  const jobSectionEnabled = useExposureFlag('ui_jobPartsSection');
  const partsAndLaborEnabled = useExposureFlag('ui_partsAndLabor');
  // FLOYD-1: asked through the same derived decision the Details tab uses, so
  // exactly one of the two ever owns Add Job. Read here rather than assumed
  // from "the panel is mounted, therefore the tab is on" — that assumption is
  // true today and is exactly the kind that stops being true silently.
  const ticketPartsPanelEnabled = useExposureFlag('ui_ticketPartsPanel');
  const addJobSurface = resolveAddJobSurface({
    partsAndLaborEnabled,
    ticketPartsPanelEnabled,
  });
  const partsAssistEnabled = useExposureFlag('ui_partsAssist');
  const partsSuggestionsEnabled = useExposureFlag('ui_partsSuggestions');
  const pensieveSuggestionsEnabled = useExposureFlag('ui_pensieveSuggestions');
  // Phase 10 R2 — sell-side labor financials gates
  const laborEngineEnabled = useFeatureFlag('labor_engine_enabled');
  const ticketFinancialsEnabled = useExposureFlag('ui_ticket_financials');
  const showLaborFinancials = laborEngineEnabled && ticketFinancialsEnabled;
  // Phase 10 R3 — tech-pay gates (manager-view also enforced inside the strip + card)
  const techPayEnabled = useExposureFlag('ui_technician_pay');
  const showTechPay = laborEngineEnabled && techPayEnabled;

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<PartsLine | null>(null);
  const [cannedOpen, setCannedOpen] = useState(false);

  const { role } = useUserRole();
  const canApplyCanned =
    role === 'OWNER' || role === 'MANAGER' || role === 'SERVICE_WRITER';

  // Phase 5 — Parts Assist intake state.
  const [assistOpen, setAssistOpen] = useState(false);
  const [assistCapture, setAssistCapture] = useState<PartsAssistCapturePayload | null>(null);

  // Pull jobs on this ticket. We fetch both the picker shape (job_id + name) and
  // the JobsSection display shape (id + name + standardMinutes + category) from
  // the same row so we don't double-query.
  const { data: ticketJobsRaw = [], isError: ticketJobsError, refetch: refetchTicketJobs } = useQuery({
    queryKey: ['ticket-jobs-for-parts', ticketId],
    enabled: !!ticketId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('ticket_jobs')
        .select('job_id, minutes_override, jobs:job_id(id, name, standard_minutes, category)')
        .eq('ticket_id', ticketId)
        .is('deleted_at', null);
      if (error) throw error;
      return data ?? [];
    },
  });

  const ticketJobs: Array<JobOption & { category?: string | null }> = ticketJobsRaw.map((r: any) => ({
    job_id: r.job_id,
    name: r.jobs?.name ?? 'Unnamed job',
    standardMinutes: r.minutes_override ?? r.jobs?.standard_minutes ?? 0,
    category: r.jobs?.category ?? null,
  }));

  const jobsForSection: JobsSectionJob[] = ticketJobsRaw.map((r: any) => ({
    id: r.job_id,
    name: r.jobs?.name ?? 'Unnamed job',
    standardMinutes: r.minutes_override ?? r.jobs?.standard_minutes ?? 0,
    category: r.jobs?.category ?? undefined,
  }));

  // Phase 4D — sum of ticket_jobs minutes (book), used as the displayed
  // labor figure in the unified rollup. Authoritative for DISPLAY ONLY.
  const laborMinutes = jobsForSection.reduce((sum, j) => sum + (j.standardMinutes || 0), 0);

  // Phase 4D — fetch tickets.predicted_minutes for dev-only soft drift check.
  // Never used as authority; only compared to laborMinutes inside PartsLaborRollup.
  const { data: ticketPredicted } = useQuery({
    queryKey: ['ticket-predicted-minutes', ticketId],
    enabled: !!ticketId && partsAndLaborEnabled,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('tickets')
        .select('predicted_minutes')
        .eq('id', ticketId)
        .maybeSingle();
      if (error) throw error;
      return (data?.predicted_minutes as number | undefined) ?? undefined;
    },
  });

  // Pull tax/fee config (Phase 1 tables; fall back to neutral defaults).
  // Phase 6B-Hardening Item 2: hard-fail on real Supabase errors so the rollup
  // can show an explicit "config could not load" warning instead of silently
  // downgrading to zero-tax math. `maybeSingle()` returning null with no error
  // is a legitimate "no row yet" state — that path keeps using defaults.
  const { data: taxConfig, isError: taxConfigError } = useQuery({
    queryKey: ['shop-tax-config-for-parts', shopId],
    enabled: !!shopId,
    retry: 1,
    queryFn: async () => {
      const [settingsRes, rulesRes, feesRes] = await Promise.all([
        (supabase as any).from('shop_tax_settings').select('*').eq('shop_id', shopId).maybeSingle(),
        (supabase as any).from('shop_tax_rules').select('*').eq('shop_id', shopId).maybeSingle(),
        (supabase as any).from('shop_fee_rules').select('*').eq('shop_id', shopId).eq('enabled', true),
      ]);
      if (settingsRes.error) throw settingsRes.error;
      if (rulesRes.error) throw rulesRes.error;
      if (feesRes.error) throw feesRes.error;
      return {
        taxSettings: (settingsRes.data as TaxSettingsLike | null) ?? DEFAULT_TAX_SETTINGS,
        taxRules: rulesRes.data?.categories
          ? { categories: rulesRes.data.categories as TaxabilityCategories }
          : DEFAULT_TAX_RULES,
        feeRules: (feesRes.data as FeeRule[] | null) ?? [],
      };
    },
  });

  const { data: lines = [], isLoading, isError: linesError, refetch: refetchLines } = useTicketPartsLines(shopId, ticketId);
  const { rollup } = useTicketPartsRollup({
    shopId,
    ticketId,
    taxRules: taxConfig?.taxRules ?? DEFAULT_TAX_RULES,
    taxSettings: taxConfig?.taxSettings ?? DEFAULT_TAX_SETTINGS,
    feeRules: taxConfig?.feeRules,
  });

  const createMut = useCreatePartsLine();
  const updateMut = useUpdatePartsLine();
  const deleteMut = useDeletePartsLine();
  const statusMut = useUpdatePartsLineStatus();

  // Phase 10 R2 — labor sell totals (only fetched when financial flags are on).
  const { data: laborData } = useTicketLaborLines(ticketId, showLaborFinancials);
  // Phase 10 R3 — tech-pay totals (manager-view check happens inside the strip / RLS).
  const techPayVisible = showTechPay && (role === 'OWNER' || role === 'MANAGER');
  const { data: techPayData } = useTicketTechPayLines(ticketId, techPayVisible);

  const ticketLevelLines = useMemo(() => lines.filter((l) => !l.job_id), [lines]);
  const linesByJob = useMemo(() => {
    const m = new Map<string, PartsLine[]>();
    for (const l of lines) {
      if (!l.job_id) continue;
      if (!m.has(l.job_id)) m.set(l.job_id, []);
      m.get(l.job_id)!.push(l);
    }
    return m;
  }, [lines]);

  // Phase 6A — derive suggestion chips from DVI findings + ticket job categories.
  // Phase 6B — extends with Pensieve "Commonly added" co-occurrence chips when
  // ui_pensieveSuggestions is on. Read-only; chip clicks open the existing
  // intake modal — never auto-add.
  const suggestions = useSuggestedParts({
    shopId,
    ticketId,
    jobOptions: ticketJobs,
    existingLines: lines,
    enabled: partsSuggestionsEnabled && partsAssistEnabled && partsLineItems,
    pensieveEnabled: pensieveSuggestionsEnabled,
  });

  /** Build a synthetic capture from a suggestion chip and route it through the
   *  same intake modal the extension uses. Explainability preserved via
   *  source_label (DVI vs Job vs Pensieve) + notes. For Pensieve chips, the
   *  notes line carries scope + frequency + sample size verbatim. */
  const handleSuggestionSelect = (s: PartSuggestion) => {
    const sourceLabel =
      s.source === 'dvi' ? 'Based on inspection'
      : s.source === 'job' ? 'Based on job'
      : 'Commonly added';

    // Pensieve notes prefer the scope-aware sentence already on s.reason.
    // DVI/Job keep the "Suggested: <reason>" prefix for backward consistency.
    const notes =
      s.source === 'pensieve' ? s.reason : `Suggested: ${s.reason}`;

    const cap: PartsAssistCapturePayload = {
      source_vendor: 'suggestion',
      source_label: sourceLabel,
      description: s.label,
      quantity: 1,
      unit_cost: null,
      part_number: null,
      availability: null,
      core_charge: null,
      notes,
      external_id: s.jobId ?? s.dviFindingId ?? null,
      captured_at: clock.now().toISOString(),
      capture_confidence: 'manual',
    };
    setAssistCapture(cap);
    setAssistOpen(true);
    // Pre-select originating job for job/pensieve-sourced suggestions via
    // sessionStorage (PartsAssistIntakeModal already reads pa:lastJob:<ticketId>).
    if ((s.source === 'job' || s.source === 'pensieve') && s.jobId) {
      try {
        sessionStorage.setItem(`pa:lastJob:${ticketId}`, s.jobId);
      } catch { /* ignore quota */ }
    }
  };

  // Phase 5 — Parts Assist postMessage bridge.
  // Listens for envelopes from the browser extension (or a manual paste tool)
  // and opens the intake modal. The bridge ONLY validates + opens the modal;
  // it never writes to the DB. The modal is the single confirmation surface.
  useEffect(() => {
    if (!partsAssistEnabled) return;
    const onMessage = (event: MessageEvent) => {
      if (!isPartsAssistMessage(event.data)) return;
      // Same-origin only — anything cross-origin is ignored.
      if (event.origin && event.origin !== window.location.origin) return;
      setAssistCapture(event.data.payload);
      setAssistOpen(true);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [partsAssistEnabled]);

  if (!shopId) return null;

  const handleSubmit = async (
    payload: PartsLineInsert | (Partial<PartsLine> & { id: string }),
  ) => {
    if ('id' in payload && payload.id) {
      const { id, shop_id: _shop, ...patch } = payload as any;
      await updateMut.mutateAsync({ id, shopId, ticketId, patch });
    } else {
      await createMut.mutateAsync(payload as PartsLineInsert);
    }
  };

  const handleDelete = (line: PartsLine) => {
    if (window.confirm(`Remove "${line.description}"?`)) {
      deleteMut.mutate({ id: line.id, shopId, ticketId });
    }
  };

  const handleStatusChange = (line: PartsLine, next: PartsLineStatus) => {
    statusMut.mutate({ id: line.id, shopId, ticketId, status: next });
  };

  const openAdd = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (line: PartsLine) => { setEditing(line); setFormOpen(true); };

  // Parts count per job_id, used by JobsSection to render the linked-parts badge.
  const partsCountByJobId: Record<string, number> = {};
  for (const l of lines) {
    if (l.job_id) partsCountByJobId[l.job_id] = (partsCountByJobId[l.job_id] ?? 0) + 1;
  }

  return (
    <ScrollArea className="h-[calc(85vh-160px)]">
      <div className="px-6 pb-6 space-y-5">
        {/* Phase 4A: Jobs section — re-homed from Details tab. FLOYD-1: owned
            by the shared placement decision, so it renders here exactly when
            the Details tab is NOT rendering it. Add Job/Add Labor/Add Part
            live here. */}
        {addJobSurface === 'parts' && (
          <JobsSection
            ticketId={ticketId}
            jobs={jobsForSection}
            partsCountByJobId={partsCountByJobId}
            onAddPart={partsLineItems ? openAdd : undefined}
          />
        )}

        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
              Parts ({lines.length})
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Internal parts list — values shown are estimates only.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {canApplyCanned && (
              <Button size="sm" variant="outline" onClick={() => setCannedOpen(true)}>
                <Plus className="h-4 w-4 mr-1" /> Add Canned Job
              </Button>
            )}
            {!partsAndLaborEnabled && (
              <RequireFeature feature="partsLineItems">
                <Button size="sm" onClick={openAdd}>
                  <Plus className="h-4 w-4 mr-1" /> Add Part
                </Button>
              </RequireFeature>
            )}
          </div>
        </div>

        {/* Phase 6B-Hardening Item 2: surface tax/fee config load failures so
            users know rollup math fell back to neutral defaults. */}
        {taxConfigError && (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
            Tax/fee config could not load — totals shown use neutral defaults and may be incorrect. Try refreshing the page.
          </div>
        )}

        {/* Phase 4D: Unified 5-section rollup when ui_partsAndLabor is on.
            Otherwise the legacy parts-only summary is preserved. */}
        {partsAndLaborEnabled ? (
          <>
            {/* Phase 10 R4 — Ticket-level GP card. Self-hides when role/flag
                gates are off or summary is loading. Mounted ABOVE the
                parts/labor rollup for visibility hierarchy. */}
            <TicketFinancialSummaryCard ticketId={ticketId} />
            <PartsLaborRollup
              laborMinutes={laborMinutes}
              predictedMinutes={ticketPredicted}
              rollup={rollup ?? null}
              laborTotals={laborData?.totals ?? null}
              showFinancials={showLaborFinancials}
              techPayTotals={techPayData?.totals ?? null}
              showTechPay={showTechPay && (role === 'OWNER' || role === 'MANAGER')}
            />
          </>
        ) : (
          rollup && lines.length > 0 && <PartsRollupSummary rollup={rollup} />
        )}

        {/* Phase 6A — Suggested parts panel. Display-only chips. Behind
            ui_partsSuggestions exposure flag (default OFF). Renders nothing
            when there are no suggestions, so zero regression when off. */}
        {partsSuggestionsEnabled && partsAssistEnabled && partsLineItems && (
          <SuggestedPartsPanel
            ticketId={ticketId}
            suggestions={suggestions}
            onSelect={handleSuggestionSelect}
          />
        )}

        {/* CORE-AE: a failed read used to render as "No parts on this job yet." — on the surface
            that feeds the invoice. Say it could not be read instead. */}
        {(linesError || ticketJobsError) && (
          <div
            role="alert"
            data-testid="ticket-parts-load-failure"
            className="flex flex-wrap items-center gap-2 rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm"
          >
            <span className="flex-1">
              Could not load {linesError && ticketJobsError ? 'parts or jobs' : linesError ? 'parts' : 'jobs'} for this ticket. What is shown may be incomplete.
            </span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => { void refetchLines(); void refetchTicketJobs(); }}
            >
              Retry
            </Button>
          </div>
        )}

        {isLoading ? (
          <div className="text-sm text-muted-foreground">Loading parts…</div>
        ) : (
          <>
            {/* Phase 7 R1 — Procurement panel: lists eligible parts (not yet on
                an active PO) with multi-select + Create PO. Hidden for techs. */}
            {lines.length > 0 && (
              <TicketProcurementPanel ticketId={ticketId} shopId={shopId} lines={lines} />
            )}

            {/* Ticket-level parts always shown */}
            <div className="space-y-2">
              {jobSectionEnabled && (
                <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Whole ticket
                </h4>
              )}
              <PartsLineList
                lines={ticketLevelLines}
                onEdit={openEdit}
                onDelete={handleDelete}
                onStatusChange={handleStatusChange}
                canMutate={partsLineItems}
                jobOptions={ticketJobs}
                emptyText="No ticket-level parts yet."
              />
            </div>

            {/* Per-job sections (gated by ui_jobPartsSection) */}
            {jobSectionEnabled && ticketJobs.map((job) => {
              const jobLines = linesByJob.get(job.job_id) ?? [];
              const jobRollup = rollup?.perJob[job.job_id];
              return (
                <Collapsible key={job.job_id} defaultOpen={jobLines.length > 0}>
                  <div className="rounded-lg border bg-muted/10 p-3 space-y-2">
                    <CollapsibleTrigger className="flex w-full items-center justify-between text-left">
                      <div>
                        <div className="text-sm font-medium">{job.name}</div>
                        <div className="text-[11px] text-muted-foreground">
                          {jobLines.length} part{jobLines.length === 1 ? '' : 's'}
                        </div>
                      </div>
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    </CollapsibleTrigger>
                    <CollapsibleContent className="space-y-2 pt-2">
                      {jobRollup && jobLines.length > 0 && (
                        <PartsRollupSummary rollup={jobRollup} title={`${job.name} estimate`} compact />
                      )}
                      <PartsLineList
                        lines={jobLines}
                        onEdit={openEdit}
                        onDelete={handleDelete}
                        onStatusChange={handleStatusChange}
                        canMutate={partsLineItems}
                        jobOptions={ticketJobs}
                        emptyText="No parts on this job yet."
                      />
                    </CollapsibleContent>
                  </div>
                </Collapsible>
              );
            })}
          </>
        )}

        {partsLineItems && (
          <PartsLineFormDialog
            open={formOpen}
            onOpenChange={setFormOpen}
            shopId={shopId}
            ticketId={ticketId}
            jobOptions={ticketJobs}
            initial={editing}
            onSubmit={handleSubmit}
          />
        )}

        {/* Phase 5 — Parts Assist intake modal. Only mounts when feature is on
            AND parts mutations are allowed (same RBAC as the regular Add Part). */}
        {partsAssistEnabled && partsLineItems && (
          <PartsAssistIntakeModal
            open={assistOpen}
            onOpenChange={(o) => {
              setAssistOpen(o);
              if (!o) setAssistCapture(null);
            }}
            shopId={shopId}
            ticketId={ticketId}
            jobOptions={ticketJobs}
            existingLines={lines}
            capture={assistCapture}
            onSubmit={async (payload) => {
              await createMut.mutateAsync(payload);
              toast({ title: 'Part added from vendor capture' });
            }}
          />
        )}

        {canApplyCanned && (
          <CannedJobSelectorDialog
            open={cannedOpen}
            onOpenChange={setCannedOpen}
            shopId={shopId}
            ticketId={ticketId}
          />
        )}
      </div>
    </ScrollArea>
  );
}
